import React from 'react';
import { formatDuration, formatFileSize } from '../../utils/audioDuration';
import { packSummary, packFinished, packRunning, STATUS_LABEL } from '../../lib/recognition/pack';

/**
 * Пакет файлов — по артборду «Бумаги» (состояния UPLOADED / PROCESSING с
 * несколькими файлами): список с состоянием каждого, одна кнопка на всех,
 * «Убрать» до старта, «Повторить» у оборвавшейся загрузки, «Открыть» у
 * готового; подпись «Вкладку можно закрыть — доделаем сами» честная: каждая
 * задача зарегистрирована под завершитель. При выключенной Истории завершитель
 * задачу не берёт (App.js, registerJob) — тогда подпись просит вкладку не закрывать.
 *
 * Чего из артборда здесь нет (к 18.09 не делали): папка целиком, расчёт
 * «спишется / останется» по всему пакету, определение стерео до старта —
 * стерео узнаётся в момент запуска файла, как у одиночной загрузки.
 */
export default function PackState({ items, diarize, onDiarizeChange, onStart, starting,
                                    onRemove, onRetry, onOpen, onNew, historyEnabled = true }) {
  const running = packRunning(items);
  const finished = packFinished(items);
  const started = running || finished || items.some((it) => it.status !== 'queued');
  const queued = items.filter((it) => it.status === 'queued').length;

  return (
    <div className="flex flex-col gap-3" role="region" aria-label="Пакет файлов">
      <div className="bg-paper-card rounded-card px-5 pt-5 pb-5 md:px-[30px] md:pt-[26px] md:pb-[22px] flex items-end justify-between gap-6 flex-wrap shrink-0">
        <div className="min-w-0">
          <div className="text-[12px] text-paper-ink-3">{started ? 'Идёт обработка' : 'Пакет собран'}</div>
          <div className="mt-2 font-display text-[28px] font-extrabold leading-none tracking-[-0.035em] text-paper-ink">
            {packSummary(items)}
          </div>
        </div>
        {!started && (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-[13px] text-paper-ink">
              <input type="checkbox" checked={diarize} onChange={(e) => onDiarizeChange(e.target.checked)} />
              Разделять участников
            </label>
            <button type="button" onClick={onStart} disabled={starting || items.length === 0}
                    className="rounded-control bg-paper-accent px-5 py-[10px] text-[14px] font-semibold text-white disabled:opacity-50">
              {starting ? 'Запускаем…' : `Распознать ${items.length}`}
            </button>
          </div>
        )}
        {finished && (
          <button type="button" onClick={onNew}
                  className="rounded-control bg-paper-bg px-4 py-[8px] text-[13px] font-medium text-paper-ink">
            Новый пакет
          </button>
        )}
      </div>

      <div className="bg-paper-card rounded-card px-[14px] py-2">
        <ul className="divide-y divide-paper-line">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-3 px-3 py-[10px] text-[13px]">
              <span className="min-w-0 flex-1 truncate text-paper-ink">{it.name}</span>
              <span className="shrink-0 font-mono text-[12px] text-paper-ink-3">
                {[it.duration ? formatDuration(it.duration) : null, formatFileSize(it.file.size)]
                  .filter(Boolean).join(' · ')}
              </span>
              <span className={`shrink-0 text-[12px] ${it.status === 'failed' ? 'text-paper-accent' : 'text-paper-ink-3'}`}>
                {it.status === 'failed' ? (it.error || STATUS_LABEL.failed) : STATUS_LABEL[it.status]}
              </span>
              {it.status === 'queued' && !started && (
                <button type="button" onClick={() => onRemove(it.id)}
                        className="shrink-0 text-[12px] text-paper-ink-3 underline underline-offset-[3px]">
                  Убрать
                </button>
              )}
              {it.status === 'failed' && it.retryable && (
                <button type="button" onClick={() => onRetry(it.id)}
                        className="shrink-0 text-[12px] text-paper-ink underline underline-offset-[3px]">
                  Повторить · минуты не списаны
                </button>
              )}
              {it.status === 'done' && (
                <button type="button" onClick={() => onOpen(it)}
                        className="shrink-0 rounded-[8px] bg-paper-bg px-3 py-[5px] text-[12px] font-medium text-paper-ink">
                  Открыть
                </button>
              )}
            </li>
          ))}
        </ul>
        {started && !finished && (
          <div className="border-t border-paper-line px-3 pt-[12px] pb-[8px] text-[13px] text-paper-ink-3">
            {historyEnabled ? (
              <>
                Готовые записи можно открывать сразу — они уже в Истории. Вкладку можно закрыть.
                {queued > 0 && ' Файлы, ещё не загруженные, ждут вкладку.'}
              </>
            ) : 'Готовые записи можно открывать сразу. Не закрывайте вкладку — История выключена, результаты останутся только здесь.'}
          </div>
        )}
      </div>
    </div>
  );
}
